import React from "react";
import { Image, Pressable, StyleSheet, Text, View } from "react-native";

import { SynapseDarkTheme } from "../theme/SynapseTheme";
import CustomIcon from "./CustomIcon";
import { BUTTON_COLORS } from "./ui/buttonColors";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; onRetry: () => void }>;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error Boundary caught an error:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallback ?? DefaultErrorFallback;
      return <Fallback error={this.state.error} onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

// Plain React Native only: this fallback can render above PaperProvider and
// ThemeProvider, so it must not depend on either (tokens are read directly).
const { colors, customColors } = SynapseDarkTheme;

const DefaultErrorFallback: React.FC<{
  error?: Error;
  onRetry: () => void;
}> = ({ error, onRetry }) => (
  <View style={[styles.container, { backgroundColor: colors.background }]}>
    <Image
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      source={require("../../assets/favicon.png")}
      style={styles.logo}
      accessibilityLabel="Synapse"
    />
    <View
      style={[
        styles.card,
        { backgroundColor: colors.surface, borderColor: colors.outline },
      ]}
    >
      <View style={styles.iconRow}>
        <CustomIcon
          source="alert-circle-outline"
          size={32}
          color={customColors.endNode}
        />
      </View>
      <Text style={[styles.title, { color: colors.onSurface }]}>
        Something went wrong
      </Text>
      <Text style={[styles.message, { color: colors.onSurfaceVariant }]}>
        An unexpected error occurred. Please try again.
      </Text>
      {__DEV__ && error && (
        <Text style={[styles.errorDetails, { color: colors.onSurfaceVariant }]}>
          {error.message}
        </Text>
      )}
      <Pressable
        accessibilityRole="button"
        onPress={onRetry}
        style={[styles.button, { backgroundColor: BUTTON_COLORS.primaryBg }]}
      >
        <Text style={[styles.buttonText, { color: BUTTON_COLORS.primaryText }]}>
          Try again
        </Text>
      </Pressable>
    </View>
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  logo: {
    width: 40,
    height: 40,
    marginBottom: 16,
  },
  card: {
    width: "100%",
    maxWidth: 400,
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
  },
  iconRow: {
    alignItems: "center",
    marginBottom: 8,
  },
  title: {
    textAlign: "center",
    marginBottom: 8,
    fontSize: 20,
    fontWeight: "700",
  },
  message: {
    textAlign: "center",
    marginBottom: 16,
    fontSize: 14,
  },
  errorDetails: {
    fontFamily: "monospace",
    fontSize: 12,
    marginBottom: 16,
    textAlign: "center",
    opacity: 0.8,
  },
  button: {
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    height: 46,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: "600",
  },
});

export default ErrorBoundary;
